/// <amd-module name="VSSUI/Components/PickList/PickList" />

import { FocusZone, FocusZoneDirection } from "OfficeFabric/FocusZone";
import { List } from "OfficeFabric/List";
import { SelectionMode } from "OfficeFabric/Selection";
import { TextField } from "OfficeFabric/TextField";
import { doesElementContainFocus, format, getFirstFocusable, IBaseProps, warn } from "OfficeFabric/Utilities";
import { TooltipHost, TooltipOverflowMode } from "OfficeFabric/Tooltip";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { TimerManagement } from "VSS/Core/TimerManagement";
import { getId } from "VSS/Core/Util/Object";
import { Button } from "VSSUI/Button";
import { Checkbox } from "VSSUI/Checkbox";
import { IItemIndicatorProps, ItemIndicatorSet } from "VSSUI/ItemIndicator";
import { RadioButton, RadioButtonGroup, RadioButtonGroupDirection } from "VSSUI/RadioButton";
import { ISpinnerProps, Spinner, SpinnerSize } from "VSSUI/Spinner";
import { arrayEquals, getSafeId, KeyCode, css } from "VSSUI/Util";
import { VssIcon, VssIconType } from "VSSUI/VssIcon";
import {
    IPickList,
    IPickListAction,
    IPickListGroup,
    IPickListIndicator,
    IPickListItem,
    IPickListOperator,
    IPickListPivot,
    IPickListProps
} from "../../Components/PickList/PickList.Props";

import * as Utils_Accessibility from "VSS/Core/Util/Accessibility";
import { FilterOperatorType } from "VSSUI/Utilities/Filter";
import * as VSSUIResources from "VSSUI/VSS.Resources.VSSUI";

import "VSS/LoaderPlugins/Css!VSSUI/Components/PickList/PickList";

/** @deprecated */
export interface IPickListState<T> {
    /**
     * The currently selected keys in the PickList
     */
    selectedKeys?: string[];

    initiallySelectedKeys?: string[];

    /**
     * The current set of items. If a promise is supplied through the items prop, then this
     * is initially empty until the promise has been resolved.
     */
    items: T[];

    /**
     * True if a promise was supplied for the items prop and it has not yet been fulfilled.
     */
    itemsLoading?: boolean;

    /**
     * The wrapped data models for all items provided to the PickList
     */
    groupDictionary?: { [key: string]: IPickListItemInternal<T>[] };

    searchResults?: any[];

    /**
     * The wrapped data models for all items provided to the PickList, given the current search text
     */
    filteredItems?: IPickListItemInternal<T>[];

    isSearchInProgress?: boolean;

    searchError?: Error;

    selectedOperatorKey?: string;

    initiallySelectedOperatorKey?: string;
}

/** @deprecated */
export const enum MessageLevel {
    none = 0,
    info,
    error
}

/** @deprecated */
export interface IPickListItemInternal<T> extends IPickListItem {
    isSelected: boolean;
    isSeparator?: boolean;
    isGroupHeader?: boolean;
    messageLevel?: MessageLevel;
    isLoadingIndicator?: boolean;
    onRender?: (element?: JSX.Element) => JSX.Element;
    data?: T;
}

const SELECT_ALL_ITEM_KEY = "select-all-item";
const DEFAULT_MIN_ITEMS_NO_SEARCH = 8;
const HIDE_LOADING_ITEMS_TIME_MS = 250;
const SEARCH_TEXT_DEBOUNCE_TIME_MS = 250;
const SEARCH_TEXT_ANNOUNCE_TIME_MS = 1000;
const ANNOUNCE_DELAY = 200;
const MAX_PIVOT_ITEM_WIDTH = 340;
const DEFAULT_ROW_HEIGHT = 32;

/** @deprecated Please use import { Dropdown } from "VSSUI/Dropdown" */
export class PickList<T = any> extends React.Component<IPickListProps<T>, IPickListState<T>> implements IPickList {
    private _id: string;
    private _focusZone = React.createRef<FocusZone>();
    private _pivotFocusZone = React.createRef<FocusZone>();
    private _textField = React.createRef<TextField>();
    private _pickListElement = React.createRef<HTMLDivElement>();
    private _operatorGroup = React.createRef<PickListOperatorGroup>();
    private _firstRow: PickListRow;
    private _isMounted: boolean;
    private _maintainsOwnState: boolean;
    private _initialItemsLoaded: boolean;
    private _focusOnInitialLoad: boolean;
    private _hideSearchingContent: boolean;
    private _searchingContentHidden: boolean;
    private _hideSearchingContentTimeout: number;
    private _groupLoadRequested: { [key: string]: boolean } = {};
    private _searchTextChangeDelegate: (searchText: string) => void;
    private _searchText: string = "";
    private _itemsAnnouncer: Utils_Accessibility.ProgressAnnouncer;
    private _selectedPivot: string = "";
    private _actionCount: number;
    private _ariaDescribedById: string;
    private _list: List;
    private _timerManagement: TimerManagement;

    // Maps item keys to pick list wrapper object so that we can return selected items to consumer
    private _itemDictionary: { [key: string]: IPickListItemInternal<T> };

    constructor(props: IPickListProps<T>) {
        super(props);

        this._id = getId("PickList");
        this._timerManagement = new TimerManagement();
        this._maintainsOwnState = false;
        this._itemDictionary = {};
        if (props.initiallySelectedItems && props.selectedItems) {
            console.warn("Please only provide ONE of initiallySelectedItems OR selectedItems as props");
        }
        if (props.initiallySelectedItems) {
            this._maintainsOwnState = true;
        }

        if (props.pivots && props.pivots.length) {
            this._selectedPivot = this._getInitialPivot(props);
        }

        this.state = {
            isSearchInProgress: false,
            initiallySelectedKeys: [],
            selectedKeys: [],
            filteredItems: [],
            searchResults: [],
            items: [],
            selectedOperatorKey: props.selectedOperatorKey,
            initiallySelectedOperatorKey: props.selectedOperatorKey
        };

        this._searchTextChangeDelegate = this._timerManagement.debounce(() => this._updateSearchText(), SEARCH_TEXT_DEBOUNCE_TIME_MS);

        this._ariaDescribedById = getId("picklist-described-by");
    }

    public clearSelection(): void {
        this._onSelectionChanged([], this.state.initiallySelectedOperatorKey!);
    }

    public reset(): void {
        this._onSelectionChanged(this.state.initiallySelectedKeys!, this.state.initiallySelectedOperatorKey!);
    }

    public selectAll(): void {
        if (this.props.selectionMode === SelectionMode.multiple) {
            // This is currently only supported for cases where all of the items are passed
            // directly to the pickList and not provided through the onSearch method. We still
            // need to handle the case where the pickList is showing items retrieved via search.
            this._onSelectionChanged(this._getKeysFromItems(this.state.items, this.state.items));
        }
    }

    public focus(forceRefocus?: boolean): boolean {
        if (!forceRefocus && this._pickListElement.current && document.activeElement && doesElementContainFocus(this._pickListElement.current)) {
            // a child of the picklist already has focus, so do not try to refocus.
            return false;
        }

        if (!this._initialItemsLoaded) {
            this._focusOnInitialLoad = true;
        }

        if (this._textField.current) {
            this._textField.current.focus();
            return true;
        } else if (this._focusZone.current) {
            if (this._operatorGroup.current) {
                this._operatorGroup.current.focus();
            } else if (this._firstRow) {
                return this._focusZone.current.focusElement(ReactDOM.findDOMNode(this._firstRow) as HTMLElement);
            } else {
                return this._focusZone.current.focus();
            }
        }

        // No items so return false
        return false;
    }

    /**
     * Scroll to list item by index
     */
    public scrollToIndex(index: number) {
        if (this._list) {
            this._list.scrollToIndex(index, () => DEFAULT_ROW_HEIGHT);
        }
    }

    /**
     * Scroll to selected item in the list
     */
    public scrollToSelected() {
        const { filteredItems } = this.state;

        if (filteredItems) {
            const firstSelectedIndex = filteredItems.findIndex(item => item.isSelected);
            if (firstSelectedIndex > 0) {
                this.scrollToIndex(firstSelectedIndex);
            }
        }
    }

    public forceUpdate(): void {
        if (this._isMounted) {
            this._rebuild(this.props);
        }
    }

    public componentWillMount() {
        this._rebuild(this.props);
    }

    public componentDidMount() {
        this._isMounted = true;

        const { pivots, onSelectedPivotChanged } = this.props;
        if (onSelectedPivotChanged && pivots && pivots.length > 0 && this._selectedPivot && this._selectedPivot !== pivots[0].id) {
            onSelectedPivotChanged(this._selectedPivot);
        }
    }

    public componentWillUnmount() {
        if (this._itemsAnnouncer) {
            this._itemsAnnouncer.cancel();
        }
        this._timerManagement.clearAllTimers();
        this._isMounted = false;
    }

    public componentWillReceiveProps(newProps: IPickListProps<T>) {
        const { selectedItems, selectedOperatorKey } = this.props;
        const haveGroupsChanged = this._haveGroupsChanged(newProps);
        this._hasNewItems(newProps).then(hasNewItems => {
            if (!this._isMounted) {
                return;
            }

            if (hasNewItems || haveGroupsChanged) {
                this._rebuild(newProps);
            } else if (!arrayEquals(selectedItems, newProps.selectedItems)) {
                const newSelectedKeys = this._getKeysFromItems(newProps.selectedItems, this.state.items);

                const filteredItems = this.state.filteredItems!.map(item => {
                    item.isSelected = newSelectedKeys.some(key => key === item.key);
                    return item;
                });

                for (const itemKey in this._itemDictionary) {
                    const item = this._itemDictionary[itemKey];
                    item.isSelected = newSelectedKeys.some(key => key === itemKey);
                }

                this.setState({
                    filteredItems: filteredItems,
                    selectedKeys: newSelectedKeys,
                    selectedOperatorKey: newProps.selectedOperatorKey
                });
            } else if (selectedOperatorKey !== newProps.selectedOperatorKey) {
                this.setState({
                    selectedOperatorKey: newProps.selectedOperatorKey
                });
            }
        });
    }

    public render() {
        const {
            getActions,
            isSearchable,
            searchBoxAriaLabel,
            onSearch,
            className,
            operators,
            minItemsForSearchBox = DEFAULT_MIN_ITEMS_NO_SEARCH,
            shouldVirtualize,
            onMouseLeave,
            onMouseEnter,
            itemAriaDescription,
            onRenderFooter
        } = this.props;
        const { filteredItems, itemsLoading } = this.state;
        const items = filteredItems ? [...filteredItems] : [];
        const pivotBar = this._getPivotBar();
        const currentPivot = this._getCurrentPivot();

        let actions: IPickListAction[] = (getActions && getActions(items)) || [];

        const searchTextPlaceholder = (currentPivot && currentPivot.searchTextPlaceholder) || this.props.searchTextPlaceholder;
        const noItemsText = (currentPivot && currentPivot.noItemsText) || this.props.noItemsText;

        if (currentPivot && currentPivot.getActions) {
            const pivotActions = currentPivot.getActions(items) || [];
            actions = pivotActions.concat(actions);
        }

        this._actionCount = actions.length;

        const actionList =
            actions.length > 0 &&
            actions.map((action, index) => {
                return <PickListAction action={action} itemIndex={index} key={String(index)} setSize={this._actionCount} />;
            });

        const operatorGroup = items && items.length > 1 && operators && (
            <PickListOperatorGroup
                operators={operators}
                selectedOperatorKey={this.state.selectedOperatorKey}
                onChange={this._onOperatorChanged}
                componentRef={component => (this._operatorGroup = component)}
            />
        );

        let itemsList = null;
        const onShouldVirtualize = typeof shouldVirtualize === "function" ? () => shouldVirtualize(items) : undefined;
        if (items.length) {
            const role = !!pivotBar ? "tabpanel" : null;
            const ariaExpanded = !!pivotBar ? true : null;
            const hasItemsClass = items[0].key !== "search-no-results" && "vss-PickList--hasItems";
            const headingIndexes = items.filter(item => item.isGroupHeader).map(item => items.indexOf(item));

            itemsList = (
                <div className={css("vss-PickList--items", hasItemsClass)} role="listbox" data-is-focusable={true} data-is-scrollable={true} tabIndex={0}>
                    <List
                        role={role}
                        aria-expanded={ariaExpanded}
                        items={items}
                        onRenderCell={(item, index) => this._onRenderCell(item, index, headingIndexes)}
                        onShouldVirtualize={onShouldVirtualize}
                        componentRef={list => (this._list = list as List)}
                    />
                </div>
            );
        } else if (noItemsText && items.length === 0 && !itemsLoading) {
            itemsList = (
                <div role="alert" aria-live="assertive" className="vss-PickList--noItemsText">
                    {noItemsText}
                </div>
            );
        }

        const style: React.CSSProperties = {};

        if (this.props.minWidth) {
            style.minWidth = this.props.minWidth + "px";
        }

        if (this.props.maxWidth) {
            style.maxWidth = this.props.maxWidth + "px";
        }

        if (this.props.width) {
            if (items.length) {
                // If we have items, we don't want the length of the items list
                // to be wider than the containing element, so use a fixed width
                style.width = this.props.width + "px";
            } else {
                // If we don't have items, we're fine with the width of the no
                // items text growing beyond the containing element, so use a min-width
                style.minWidth = this.props.width + "px";
            }
        }

        if (this.props.maxHeight) {
            style.maxHeight = `${this.props.maxHeight}px`;
        }

        return (
            <div
                className={css("vss-PickList", className)}
                style={style}
                onKeyDown={this._onKeyDown}
                ref={this._pickListElement}
                id={getSafeId(this.props.id || this._id)}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
            >
                {itemAriaDescription && (
                    <div className="vss-PickList--ariaDescription" id={getSafeId(this._ariaDescribedById)}>
                        {itemAriaDescription}
                    </div>
                )}
                <FocusZone
                    className="vss-PickList--focusContainer"
                    ref={this._focusZone}
                    isInnerZoneKeystroke={ev => ev.which === KeyCode.rightArrow}
                    direction={FocusZoneDirection.vertical}
                    onActiveElementChanged={this._onActiveElementChanged}
                    isCircularNavigation={true}
                >
                    {isSearchable && (this.state.items.length >= minItemsForSearchBox || onSearch) && (
                        <div className="vss-PickList--searchSection">
                            <div className="vss-PickList--searchTextContainer flex-row flex-center">
                                <VssIcon className="flex-noshrink vss-PickList--searchTextIcon" iconName="search" iconType={VssIconType.fabric} />
                                <TextField
                                    className="vss-PickList--searchTextField flex-grow"
                                    ref={this._textField}
                                    placeholder={searchTextPlaceholder}
                                    value={this._searchText}
                                    ariaLabel={searchBoxAriaLabel || searchTextPlaceholder}
                                    onChanged={this._onSearchTextChanged}
                                    onKeyDown={this._onKeyDown}
                                />
                            </div>
                        </div>
                    )}
                    {operatorGroup}
                    {pivotBar}
                    {itemsList}
                    {actionList && (
                        <div className="vss-PickList--actions" role="listbox">
                            {actionList}
                        </div>
                    )}
                    {onRenderFooter && onRenderFooter()}
                </FocusZone>
            </div>
        );
    }

    private _getPivotBar() {
        const components: JSX.Element[] = [];
        if (this.props.pivots && (this.props.limitSearchToPivot || !this._searchText)) {
            this.props.pivots.forEach(pivot => {
                const isSelected = this._selectedPivot === pivot.id;
                components.push(
                    <Button
                        id={pivot.id}
                        key={pivot.id}
                        className={css("vss-Picklist--pivotBar-button", isSelected ? "selected" : "")}
                        onClick={this._onPivotClick.bind(this, pivot.id)}
                        ariaLabel={pivot.name}
                        role="tab"
                        aria-selected={isSelected}
                        aria-posinset={components.length}
                        aria-setsize={this.props.pivots.length}
                    >
                        <span className="innerContainer">
                            <span className="text" style={{ maxWidth: pivot.maxWidth || MAX_PIVOT_ITEM_WIDTH }}>
                                <TooltipHost overflowMode={TooltipOverflowMode.Parent} content={pivot.name}>
                                    {pivot.name}
                                </TooltipHost>
                            </span>
                        </span>
                    </Button>
                );
            });
        }
        if (components.length) {
            return (
                <div className="vss-PickList--pivotBar" role="tablist" data-is-focusable={true} onFocus={this._onPivotBarFocused}>
                    <FocusZone direction={FocusZoneDirection.horizontal} ref={this._pivotFocusZone}>
                        {components}
                    </FocusZone>
                </div>
            );
        }
    }

    private _onPivotBarFocused = () => {
        if (this._pivotFocusZone.current) {
            this._pivotFocusZone.current.focus();
        }
    };

    /**
     * Handles the onClick event on the pivot button.
     */
    private _onPivotClick(itemKey: string, ev: React.MouseEvent<HTMLElement>) {
        ev.preventDefault();
        this._updateSelectedPivot(itemKey);
    }

    private _updateSelectedPivot(itemKey: string) {
        if (this._selectedPivot !== itemKey) {
            this._selectedPivot = itemKey;
            this.props.onSelectedPivotChanged && this.props.onSelectedPivotChanged(itemKey);

            if (this.props.limitSearchToPivot && this._searchText) {
                this._updateSearchText();
            }

            this.scrollToIndex(0);
            this.forceUpdate();
        }
    }

    private _getInitialPivot({ selectedItems, initiallySelectedItems, getListItem, groups, pivots }: IPickListProps<T>): string {
        const firstSelectedItem = (selectedItems && selectedItems[0]) || (initiallySelectedItems && initiallySelectedItems[0]);

        if (firstSelectedItem && getListItem) {
            const { groupKey } = getListItem(firstSelectedItem);
            if (groupKey) {
                const group = groups.filter(g => g.key === groupKey)[0];
                if (group && group.pivotId) {
                    return group.pivotId;
                }
            }
        }

        return pivots[0].id;
    }

    private _getCurrentPivot(): IPickListPivot {
        return this.props.pivots && this.props.pivots.filter(p => p.id === this._selectedPivot)[0];
    }

    private _rebuild(props: IPickListProps<T>) {
        if (this.props.items !== props.items && this._itemsAnnouncer) {
            this._itemsAnnouncer.cancel();
            this._itemsAnnouncer = null;
        }
        if (this._isThenable(props.items)) {
            this._buildFromItems(props, this.state.items, true);

            (props.items as Promise<T[]>).then(
                resolvedItems => {
                    // Ensure that the items promise prop is still current
                    if (this.props.items === props.items) {
                        this._buildFromItems(props, resolvedItems, false);
                    }
                },
                rejectReason => {
                    // Ensure that the items promise prop is still current
                    if (this.props.items === props.items) {
                        this._buildFromItems(props, [], false);
                        console.warn(
                            `Failed to get items for PickList: ${rejectReason && rejectReason.message ? rejectReason.message : rejectReason}`
                        );
                    }
                }
            );
            if (!this._itemsAnnouncer) {
                const announcer = Utils_Accessibility.ProgressAnnouncer.forPromise(props.items as Promise<any[]>, {
                    announceStartMessage: VSSUIResources.AnnounceLoadingItems,
                    announceEndMessage: VSSUIResources.AnnounceFinishedLoadingItems,
                    announceErrorMessage: VSSUIResources.AnnounceErrorLoadingItems
                });
                this._itemsAnnouncer = announcer;
            }
        } else {
            this._buildFromItems(props, (props.items as any[]) || [], false);
        }
    }

    private _buildFromItems(props: IPickListProps<T>, items: T[], loading: boolean) {
        const initialItemsPreviouslyLoaded = this._initialItemsLoaded;
        let initiallySelectedKeys = this.state.initiallySelectedKeys;
        let selectedKeys = this.state.selectedKeys;
        let selectedOperatorKey = this.state.selectedOperatorKey;

        if (!loading && !initialItemsPreviouslyLoaded && items.length) {
            if (props.initiallySelectedItems) {
                initiallySelectedKeys = this._getKeysFromItems(props.initiallySelectedItems, items);
                selectedKeys = initiallySelectedKeys;
            }
            this._initialItemsLoaded = true;
        }

        if (!this._maintainsOwnState) {
            selectedKeys = this._getKeysFromItems(props.selectedItems, items);
            selectedOperatorKey = props.selectedOperatorKey;
        }

        const { searchResults, isSearchInProgress, itemsLoading } = this.state;
        const wrappedItems = this._wrapItems(items, selectedKeys);
        const searchWrappedItems = this._wrapItems(searchResults, selectedKeys, "search-results");
        const groupDictionary = this._groupItems(wrappedItems);
        const groupDictionaryWithSearchResults = this._groupItems(searchWrappedItems, groupDictionary);
        const filteredGroupDictionary = this._filterByText(this._searchText, groupDictionaryWithSearchResults);
        const groupedItems = this._buildGroupedItems(
            this._searchText,
            isSearchInProgress,
            searchResults,
            filteredGroupDictionary,
            props.groups,
            selectedKeys,
            loading
        );
        // Only announce if items were loading and just finished
        if (itemsLoading && this._searchText) {
            this._announceSearchResults(filteredGroupDictionary, isSearchInProgress, loading);
        }

        this.setState(
            {
                items,
                groupDictionary,
                initiallySelectedKeys,
                filteredItems: groupedItems,
                selectedKeys: selectedKeys,
                itemsLoading: loading,
                selectedOperatorKey: selectedOperatorKey
            },
            () => {
                if (!initialItemsPreviouslyLoaded && this._initialItemsLoaded && this._focusOnInitialLoad) {
                    // We want to force a re-focus, just in case something else in the PickList has grabbed focus
                    // before the initial items have been loaded. Otherwise, we can get in a state where the
                    // first action has focus instead of the first item or search box.
                    this.focus(true);
                }
            }
        );
    }

    private _onKeyDown = (ev: React.KeyboardEvent<HTMLElement>) => {
        if (ev.which === KeyCode.tab) {
            const { onFocusLost } = this.props;
            if (onFocusLost) {
                onFocusLost();
            }
        } else if (ev.which === KeyCode.enter && this.props.selectExactMatchOnEnter && this._searchText && this.state.filteredItems) {
            const exactMatches = this.state.filteredItems.filter(item => this._isExactMatch(this._searchText, item));
            if (exactMatches.length === 1) {
                this._onSelectionChanged([exactMatches[0].key]);
            }
        }
    };

    private _onSearchTextChanged = (searchText: string) => {
        this._searchText = searchText;
        this._searchTextChangeDelegate(searchText);
    };

    private _onClearInputClick = () => {
        this._searchText = "";
        this._updateSearchText();

        if (this._textField.current) {
            this._textField.current.focus();
        }
    };

    private _updateSearchText = (resultsToContinue?: any[]) => {
        const { groups, onSearch } = this.props;
        let { isSearchInProgress } = this.state;
        const searchText = this._searchText;

        if (searchText) {
            if (onSearch) {
                window.setTimeout(() => {
                    if (this.state.isSearchInProgress) {
                        Utils_Accessibility.announce(VSSUIResources.Searching, true, ANNOUNCE_DELAY);
                    }
                }, SEARCH_TEXT_ANNOUNCE_TIME_MS);

                isSearchInProgress = true;

                /**
                 * Set _hideSearchingContent to true to indicate to us to not render search loading indicator.
                 * This state is set for a short amount of time on initial render to avoid flickering
                 * of the progress indicator when the search can be completed quickly.
                 */
                if (this._hideSearchingContentTimeout) {
                    window.clearTimeout(this._hideSearchingContentTimeout);
                    this._hideSearchingContentTimeout = 0;
                }
                this._hideSearchingContent = true;
                this._hideSearchingContentTimeout = window.setTimeout(() => {
                    this._hideSearchingContent = false;
                    if (this._searchingContentHidden) {
                        this.forceUpdate();
                    }
                }, HIDE_LOADING_ITEMS_TIME_MS);

                this._getOnSearchPromise(searchText, resultsToContinue).then(
                    searchResults => {
                        // Ignore search results from previous searches. Only update results if the search string is still current
                        if (searchText === this._searchText) {
                            if (!searchResults) {
                                this.setState({ isSearchInProgress: false });
                            } else {
                                isSearchInProgress = false;
                                const { groupDictionary, itemsLoading, selectedKeys } = this.state;
                                const searchPickListItems = this._wrapItems(searchResults, selectedKeys, "search-results");
                                const groupDictionaryWithSearchResults = this._groupItems(searchPickListItems, groupDictionary);
                                const filteredGroupDictionary = this._filterByText(searchText, groupDictionaryWithSearchResults);
                                const groupedItems = this._buildGroupedItems(
                                    searchText,
                                    isSearchInProgress,
                                    searchResults,
                                    filteredGroupDictionary,
                                    groups,
                                    selectedKeys,
                                    itemsLoading
                                );

                                this._announceSearchResults(filteredGroupDictionary, isSearchInProgress, itemsLoading);
                                this.setState({ isSearchInProgress, filteredItems: groupedItems, searchResults, searchError: undefined }, () =>
                                    this._rebuild(this.props)
                                );
                            }
                        }
                    },
                    error => {
                        this.setState({ isSearchInProgress: false, searchError: error }, () => this._rebuild(this.props));
                    }
                );
            }
        }

        const { groupDictionary, itemsLoading, selectedKeys } = this.state;
        const filteredGroupDictionary = this._filterByText(searchText, groupDictionary!);
        const groupedItems = this._buildGroupedItems(searchText, isSearchInProgress, [], filteredGroupDictionary, groups, selectedKeys, itemsLoading);

        // If scroll were kept at a high index, it will make "Loading" visible initially
        // which may result in a cascade of loading pages until they reach such index.
        this.scrollToIndex(0);

        this._announceSearchResults(filteredGroupDictionary, isSearchInProgress, itemsLoading);

        this.setState(
            {
                filteredItems: groupedItems,
                isSearchInProgress,
                searchResults: resultsToContinue ? this.state.searchResults : []
            },
            () => this._rebuild(this.props)
        );
    };

    private _announceSearchResults(
        filteredGroupDictionary: { [key: string]: IPickListItemInternal<T>[] },
        isSearchInProgress: boolean,
        itemsLoading: boolean
    ): void {
        if (!isSearchInProgress && !itemsLoading) {
            let resultCount = 0;
            for (const key in filteredGroupDictionary) {
                resultCount += filteredGroupDictionary[key].length;
            }
            Utils_Accessibility.announce(
                resultCount > 0 ? format(VSSUIResources.AnnounceFilterResultCount, resultCount) : VSSUIResources.NoFilterResults,
                true,
                ANNOUNCE_DELAY
            );
        }
    }

    private _wrapItems(items: T[], selectedKeys: string[], overrideGroupKey?: string): IPickListItemInternal<T>[] {
        const { getListItem, onSearch } = this.props;
        const pickListItems: IPickListItemInternal<T>[] = items.map(item => {
            const pickListItem: IPickListItem = getListItem
                ? getListItem(item)
                : {
                      name: item as any as string,
                      key: item as any as string
                  };
            if (!pickListItem.groupKey) {
                pickListItem.groupKey = "undefined-key";
            }
            const pickListItemInternal: IPickListItemInternal<T> = {
                ...pickListItem,
                isSelected: selectedKeys!.some(key => key === pickListItem.key),
                data: item
            };

            if (overrideGroupKey) {
                pickListItemInternal.groupKey = overrideGroupKey;
            }

            this._itemDictionary[pickListItem.key] = pickListItemInternal;

            return pickListItemInternal;
        });

        return pickListItems;
    }

    private _groupItems(
        items: IPickListItemInternal<T>[],
        initialGroupDictionary?: { [key: string]: IPickListItemInternal<T>[] }
    ): { [key: string]: IPickListItemInternal<T>[] } {
        const groupDictionary: { [key: string]: IPickListItemInternal<T>[] } = {};
        if (initialGroupDictionary) {
            for (const key in initialGroupDictionary) {
                groupDictionary[key] = [...initialGroupDictionary[key]];
            }
        }
        if (items && items.length) {
            items.forEach(item => {
                const groupKey = item.groupKey!;
                if (!groupDictionary[groupKey]) {
                    groupDictionary[groupKey] = [];
                }
                groupDictionary[groupKey].push(item);
            });
        }
        return groupDictionary;
    }

    private _filterByText(
        searchText: string | undefined,
        groupDictionary: { [key: string]: IPickListItemInternal<T>[] }
    ): { [key: string]: IPickListItemInternal<T>[] } {
        let filteredGroupDictionary: { [key: string]: IPickListItemInternal<T>[] } = {};

        if (searchText) {
            const onFilterItemByText = this.props.onFilterItemByText || this._defaultFilterItemByText;

            for (const key in groupDictionary) {
                if (this._selectedPivot && this.props.limitSearchToPivot && key !== "search-results") {
                    const group = this.props.groups.filter(g => g.key === key)[0];
                    if (!group || group.pivotId !== this._selectedPivot) {
                        continue;
                    }
                }

                const groupItems = groupDictionary[key];
                const filteredGroupItems = groupItems.filter(item => {
                    return item.isSeparator || onFilterItemByText(searchText, item, item.data);
                });
                filteredGroupDictionary[key] = filteredGroupItems;
            }
        } else {
            filteredGroupDictionary = groupDictionary;
        }

        return filteredGroupDictionary;
    }

    private _defaultFilterItemByText = (searchText: string, item: IPickListItem): boolean => {
        return item.name.toLowerCase().indexOf(searchText.toLowerCase()) !== -1;
    };

    private _isExactMatch(searchText: string, item: IPickListItem): boolean {
        return item.name.toLowerCase() === searchText.toLowerCase();
    }

    private _buildGroupedItems(
        searchText: string | undefined,
        isSearchInProgress: boolean,
        searchResults: any[],
        groupDictionary: { [key: string]: IPickListItemInternal<T>[] },
        groups: IPickListGroup[] | undefined,
        selectedKeys: string[],
        itemsLoading: boolean
    ): IPickListItemInternal<T>[] {
        let groupedItems: IPickListItemInternal<T>[] = [];
        const { itemsLoadingText, onSearch, onHasMoreSearchResults, searchResultsGroupName, searchResultsLoadingText, selectionMode, showSelectAll } =
            this.props;

        const currentPivot = this._getCurrentPivot();
        const searchNoResultsText = format(
            (currentPivot && currentPivot.searchNoResultsText) || this.props.searchNoResultsText || VSSUIResources.NoFilterResults,
            searchText || ""
        );

        const allGroups: IPickListGroup[] = [{ key: "undefined-key" }].concat(groups || []);
        if (onSearch) {
            allGroups.push({
                key: "search-results",
                name: searchResultsGroupName
            });
        }

        let needsSeparator = false;
        let groupLoadingIndicatorAdded = false;

        this._searchingContentHidden = false;

        allGroups.forEach(group => {
            const groupKey = group.key;
            const itemsForGroup = groupDictionary[groupKey];
            const isGroupVisible =
                !this._selectedPivot ||
                this._selectedPivot === group.pivotId ||
                (this._searchText && !this.props.limitSearchToPivot) ||
                group.key === "search-results";

            if (this._selectedPivot && !group.pivotId && groupKey !== "search-results" && itemsForGroup && itemsForGroup.length) {
                warn(
                    "Picklist pivots are being used and items were provided with no pivot id.  All items should be within a group with a valid pivot id"
                );
            }
            if (itemsForGroup && itemsForGroup.length && isGroupVisible) {
                if ((needsSeparator && groupKey !== "search-results") || group.name) {
                    // Don't add separator in front of search results, this is a special group with online results
                    // that doesn't need to be separated from local results.
                    groupedItems.push({
                        isSeparator: true,
                        isGroupHeader: true,
                        name: group.name,
                        key: "group-name-" + group.key,
                        data: undefined,
                        isSelected: false,
                        onRender: group.onRenderGroupHeader ? () => group.onRenderGroupHeader(group) : undefined
                    });
                }
                groupedItems = groupedItems.concat(itemsForGroup);
                needsSeparator = true;

                // Pagination: If a group has items but it's also loading, show loading indicator below the current items.
                // This indicator will request a new group load on render. Also for search-results based on onHasMoreSearchResults.
                if (group.isLoading && !(this._searchText && onSearch)) {
                    groupedItems.push({
                        isSeparator: true,
                        isLoadingIndicator: true,
                        name: group.loadingMessage || itemsLoadingText || VSSUIResources.Loading,
                        key: "items-loading-" + group.key,
                        data: undefined,
                        isSelected: false,
                        onRender: element => {
                            this._requestGroupLoad(group.key);
                            return element;
                        }
                    });
                } else if (groupKey === "search-results" && onHasMoreSearchResults && onHasMoreSearchResults(searchResults)) {
                    groupedItems.push({
                        isSeparator: true,
                        isLoadingIndicator: true,
                        name: searchResultsLoadingText || VSSUIResources.Searching,
                        key: "search-loading-" + group.key,
                        data: undefined,
                        isSelected: false,
                        onRender: element => {
                            if (!isSearchInProgress) {
                                this._updateSearchText(searchResults);
                            }
                            return element;
                        }
                    });
                }
            } else if (groupKey === "search-results") {
                if (searchText && searchText.length && onSearch) {
                    if (isSearchInProgress && this._hideSearchingContent) {
                        this._searchingContentHidden = true;
                    } else if (isSearchInProgress || (!isSearchInProgress && groupedItems.length === 0)) {
                        if (needsSeparator || group.name) {
                            groupedItems.push({
                                isSeparator: true,
                                isGroupHeader: true,
                                name: group.name || "",
                                key: "group-name-" + group.key,
                                isSelected: false
                            });
                        }

                        if (isSearchInProgress) {
                            groupedItems.push({
                                isSeparator: true,
                                isLoadingIndicator: true,
                                name: searchResultsLoadingText || VSSUIResources.Searching,
                                key: "search-loading-" + group.key,
                                data: undefined,
                                isSelected: false
                            });
                        } else if (this.state.searchError) {
                            groupedItems.push({
                                isSeparator: true,
                                messageLevel: MessageLevel.error,
                                name: this.state.searchError.message,
                                key: "search-error-" + group.key,
                                data: undefined,
                                isSelected: false
                            });
                        } else {
                            groupedItems.push({
                                isSeparator: true,
                                messageLevel: MessageLevel.info,
                                name: searchNoResultsText,
                                key: "search-no-results-" + group.key,
                                data: undefined,
                                isSelected: false
                            });
                        }

                        needsSeparator = true;
                    }
                }
            } else if (
                (itemsLoading && group.key === "undefined-key" && !this._selectedPivot) ||
                (group.isLoading && isGroupVisible && !(this._searchText && onSearch))
            ) {
                if (!groupLoadingIndicatorAdded) {
                    // Only add loading spinner for the first loading group rather than n spinners.
                    groupLoadingIndicatorAdded = true;

                    if (needsSeparator || group.name) {
                        groupedItems.push({
                            isSeparator: true,
                            isGroupHeader: true,
                            name: group.name,
                            key: "group-name-" + group.key,
                            data: undefined,
                            isSelected: false
                        });
                    }

                    groupedItems.push({
                        isSeparator: true,
                        isLoadingIndicator: true,
                        name: group.loadingMessage || itemsLoadingText || VSSUIResources.Loading,
                        key: "items-loading-" + group.key,
                        data: undefined,
                        isSelected: false,
                        onRender: element => {
                            this._requestGroupLoad(group.key);
                            return element;
                        }
                    });

                    needsSeparator = true;
                }
            }
        });

        // No custom onSearch but a filter was supplied which matched no values. Add
        // a message indicating this.
        if (searchText && searchText.length && !isSearchInProgress && groupedItems.length === 0) {
            groupedItems.push({
                isSeparator: true,
                messageLevel: MessageLevel.info,
                name: searchNoResultsText,
                key: "search-no-results",
                data: undefined,
                isSelected: false
            });
        }

        if (selectionMode === SelectionMode.multiple && showSelectAll && groupedItems.length > 1) {
            const isSelected = selectedKeys.indexOf(SELECT_ALL_ITEM_KEY) !== -1;
            const selectAllItem = {
                key: SELECT_ALL_ITEM_KEY,
                name: VSSUIResources.PickListSelectAllActionText,
                isSelected: !!isSelected
            };

            groupedItems.splice(0, 0, selectAllItem);

            this._itemDictionary[SELECT_ALL_ITEM_KEY] = selectAllItem;
        }

        return groupedItems;
    }

    private _requestGroupLoad(groupKey: string): void {
        if (this.props.onGroupLoadRequest && !this._groupLoadRequested[groupKey] && !this.state.itemsLoading) {
            this._groupLoadRequested[groupKey] = true;
            const previousItemsLength = this.state.items.length;
            this.props.onGroupLoadRequest(groupKey).then(() => {
                // Reset the flag, so we can request it again (another page) if group is still loading
                delete this._groupLoadRequested[groupKey];
                if (this.state.items.length > previousItemsLength) {
                    // If the loading indicator is still visible (because we fetched very few items), then we request again (through re-render)
                    // Only if we load new items, which prevents infinite recursion (render loading -> request group -> force update -> render loading)
                    // If onGroupLoadRequest didn't return new items, we'll continue displaying the loading until externally something changes.
                    this.forceUpdate();
                }
            });
        }
    }

    private _getOnSearchPromise(searchText: string, resultsToContinue: any[]): Promise<any[] | undefined> {
        const { onSearch } = this.props;
        const searchItems = onSearch ? onSearch(searchText, this.state.items, this._selectedPivot, resultsToContinue) : [];
        return Promise.resolve(searchItems);
    }

    private _onSelectionChanged = (newSelectedKeys: string[], selectedOperatorKey?: string): boolean => {
        let preventDefault = false;
        if (this._isMounted) {
            const { onSelectionChanged } = this.props;
            const newSelectedOperatorKey = selectedOperatorKey || this.state.selectedOperatorKey;
            if (onSelectionChanged) {
                const selectedItems = newSelectedKeys
                    .filter(key => key !== SELECT_ALL_ITEM_KEY && !!this._itemDictionary[key])
                    .map(key => this._itemDictionary[key].data);
                preventDefault =
                    onSelectionChanged({ selectedItems: selectedItems, selectedOperatorKey: newSelectedOperatorKey as FilterOperatorType }) === true;
            }

            if (this._maintainsOwnState && this._isMounted) {
                const filteredItems = this.state.filteredItems!.map(item => {
                    item.isSelected = newSelectedKeys.some(key => key === item.key);
                    return item;
                });

                this.setState({
                    filteredItems: [...this.state.filteredItems!],
                    selectedKeys: newSelectedKeys,
                    selectedOperatorKey: newSelectedOperatorKey
                });
            }
        }
        return preventDefault;
    };

    private _onOperatorChanged = (newSelectedOperatorKey: string) => {
        this._onSelectionChanged(this.state.selectedKeys, newSelectedOperatorKey);
    };

    private _onRenderCell = (item: IPickListItemInternal<T>, index: number, headingIndexes: number[]): JSX.Element => {
        const { selectionMode } = this.props;

        if (item.isSeparator) {
            if (item.isGroupHeader) {
                if (item.onRender) {
                    return item.onRender();
                } else if (item.name) {
                    return (
                        <div role="heading" aria-level={2} className={css("vss-PickList--groupHeader", { "first-group": index === 0 })}>
                            {item.name}
                        </div>
                    );
                } else {
                    return <div className="vss-PickList--groupSeparator" />;
                }
            } else if (item.isLoadingIndicator) {
                const div = (
                    <div className="vss-PickList--loading">
                        <DelayedSpinner size={SpinnerSize.medium} label={item.name} />
                    </div>
                );
                return item.onRender ? item.onRender(div) : div;
            } else if (item.messageLevel) {
                return <div className={css("vss-PickList--message", item.messageLevel === MessageLevel.error && "is-error")}>{item.name}</div>;
            } else {
                return <div>{item.name}</div>;
            }
        }

        const firstInGroup = index === 0 || this.state.filteredItems[index - 1].isSeparator;

        // Determine location in group
        const containsNoGroups = headingIndexes.length === 0;
        const groupsBeforeElement = containsNoGroups ? 0 : headingIndexes.filter(x => x < index).length;
        const actualIndex = index - groupsBeforeElement;
        const actualSize = this.state.filteredItems.length - headingIndexes.length;

        if (selectionMode === SelectionMode.multiple) {
            return this._onRenderCellMultipleSelection(item, firstInGroup, actualIndex, actualSize);
        }

        return this._onRenderCellSingleSelection(item, firstInGroup, actualIndex, actualSize);
    };

    private _onRenderCellSingleSelection = (item: IPickListItemInternal<T>, firstInGroup: boolean, index: number, setSize: number): JSX.Element => {
        const indicators = this._getIndicators(item, this.props.indicators);
        const disabled: boolean = typeof item.disabled === "function" ? item.disabled() : item.disabled;
        return (
            <PickListRow
                ariaLabel={item.ariaLabel || item.name}
                ariaDescribedBy={this.props.itemAriaDescription && this._ariaDescribedById}
                ref={row => {
                    if (index === 0) {
                        this._firstRow = row;
                    }
                }}
                role={"option"}
                dataSelectionKey={item.key}
                isSelected={item.isSelected}
                setSize={setSize}
                itemIndex={index}
                href={item.href}
                onRowFocused={(stat: boolean) => {}}
                disabled={disabled}
                firstInGroup={firstInGroup}
                onKeyboardEnter={() => {
                    return this._onSingleSelectionChanged(item.key, disabled);
                }}
            >
                <PickListSelectableElement
                    item={item}
                    indicators={indicators}
                    onSelectionChanged={() => {
                        return this._onSingleSelectionChanged(item.key, disabled);
                    }}
                    onRenderItemText={this.props.onRenderItemText}
                />
            </PickListRow>
        );
    };

    private _onRenderCellMultipleSelection = (item: IPickListItemInternal<T>, firstInGroup: boolean, index: number, setSize: number): JSX.Element => {
        const indicators = this._getIndicators(item, this.props.indicators);
        const disabled: boolean = typeof item.disabled === "function" ? item.disabled() : item.disabled;
        const pickListOption = React.createRef<PickListCheckboxElement<T>>();
        return (
            <PickListRow
                ariaLabel={item.ariaLabel || item.name}
                ariaDescribedBy={this.props.itemAriaDescription && this._ariaDescribedById}
                ref={row => {
                    if (index === 0) {
                        this._firstRow = row;
                    }
                }}
                role={"option"}
                dataSelectionKey={item.key}
                spaceBarTriggersClick={true}
                isSelected={item.isSelected}
                setSize={setSize}
                itemIndex={index}
                disabled={disabled}
                firstInGroup={firstInGroup}
                //passing state of parent element on focus
                onRowFocused={(stat: boolean) => {
                    pickListOption.current?.rowFocusState(stat);
                }}
                onKeyboardEnter={() => {
                    return this._onMultipleSelectionChanged(item.key, !item.isSelected, disabled);
                }}
            >
                <PickListCheckboxElement
                    ref={pickListOption}
                    item={item}
                    indicators={indicators}
                    onSelectionChanged={isSelected => {
                        return this._onMultipleSelectionChanged(item.key, isSelected, disabled);
                    }}
                />
            </PickListRow>
        );
    };

    private _onSingleSelectionChanged(key: string, disabled: boolean): boolean {
        if (disabled) {
            return false;
        }

        return this._onSelectionChanged([key]);
    }

    private _onMultipleSelectionChanged(key: string, isSelected: boolean, disabled: boolean): boolean {
        if (disabled) {
            return false;
        }

        let newSelectedKeys: string[] = [];
        if (key === SELECT_ALL_ITEM_KEY) {
            if (isSelected) {
                newSelectedKeys = this._getKeysFromItems(this.state.items, this.state.items);
                Utils_Accessibility.announce(VSSUIResources.AnnouncePickListSelectAllSelect, true);
            } else {
                newSelectedKeys = [];
                Utils_Accessibility.announce(VSSUIResources.AnnouncePickListSelectAllDeselect, true);
            }
        } else {
            newSelectedKeys = this.state.selectedKeys!.filter(key => key !== SELECT_ALL_ITEM_KEY);
            if (isSelected) {
                newSelectedKeys.push(key);
                Utils_Accessibility.announce(VSSUIResources.AnnouncePickListItemSelect, true);
            } else {
                newSelectedKeys.splice(newSelectedKeys.indexOf(key), 1);
                Utils_Accessibility.announce(VSSUIResources.AnnouncePickListItemDeselect, true);
            }

            if (newSelectedKeys.length === this.state.items.length) {
                newSelectedKeys = [SELECT_ALL_ITEM_KEY, ...newSelectedKeys];
            }
        }
        return this._onSelectionChanged(newSelectedKeys);
    }

    private _onActiveElementChanged = (element: HTMLElement) => {
        if (this.props.selectionMode === SelectionMode.single && this.props.selectOnFocus) {
            const key = element.getAttribute("data-selection-key") as string;
            if (key) {
                this._onSelectionChanged([key]);
            }
        }
    };

    private _getIndicators(pickListItem: IPickListItemInternal<T>, indicators?: IPickListIndicator<T>[]): IItemIndicatorProps[] {
        return indicators
            ? indicators.map(indicator => {
                  if (pickListItem.data) {
                      return indicator.getItemIndicator(pickListItem.data);
                  }
              })
            : [];
    }

    private _getKeysFromItems(items: T[] | undefined, allItems: T[] | undefined): string[] {
        if (items) {
            let selectedKeys: string[] = [];
            const { getListItem } = this.props;
            if (getListItem) {
                selectedKeys = items.map(item => getListItem(item).key);
            } else {
                selectedKeys = items as any[] as string[];
            }

            if (allItems && selectedKeys.length === allItems.length) {
                selectedKeys = [SELECT_ALL_ITEM_KEY, ...selectedKeys];
            }

            return selectedKeys;
        }
        return [];
    }

    private _hasNewItems(newProps: IPickListProps<T>): Promise<boolean> {
        if (this._isThenable(newProps.items)) {
            return (newProps.items as Promise<T[]>).then((value: T[]) => {
                return !arrayEquals(value, this.state.items);
            });
        } else {
            return Promise.resolve(!arrayEquals(this.state.items, newProps.items as T[]));
        }
    }

    private _haveGroupsChanged(newProps: IPickListProps<T>): boolean {
        const newGroups = newProps.groups;
        const oldGroups = this.props.groups;
        if (!newGroups && !oldGroups) {
            return false;
        } else if (!newGroups || !oldGroups) {
            return true;
        } else {
            if (newGroups.length !== oldGroups.length) {
                return true;
            } else {
                return newGroups.some(
                    (newGroup, index) =>
                        oldGroups[index].key !== newGroup.key ||
                        oldGroups[index].name !== newGroup.name ||
                        oldGroups[index].isLoading !== newGroup.isLoading
                );
            }
        }
    }

    private _isThenable(obj: any): boolean {
        return obj && typeof obj["then"] === "function";
    }
}

interface IPickListRowProps extends IBaseProps {
    onKeyboardEnter: () => boolean;
    href?: string;
    spaceBarTriggersClick?: boolean;
    dataSelectionKey: string;
    ariaLabel: string;
    ariaDescribedBy: string;
    isSelected: boolean;
    setSize: number;
    itemIndex: number;
    disabled: boolean;
    firstInGroup: boolean;
    role: string;
    onRowFocused: (focusStatus: boolean) => void;
}

class PickListRow extends React.Component<IPickListRowProps> {
    private _rowDiv: HTMLDivElement;
    public componentDidMount(): void {
        if (this._rowDiv) {
            this._rowDiv.addEventListener(
                "focus",
                () => {
                    this.props.onRowFocused(true);
                },
                true
            );
            this._rowDiv.addEventListener(
                "blur",
                () => {
                    this.props.onRowFocused(false);
                },
                true
            );
        }
    }
    public render(): JSX.Element {
        return (
            <div
                role={this.props.role}
                aria-checked={this.props.role == "checkbox" ? this.props.isSelected : undefined}
                aria-selected={this.props.role == "option" ? this.props.isSelected : undefined}
                aria-label={this.props.ariaLabel}
                aria-describedby={getSafeId(this.props.ariaDescribedBy)}
                aria-setsize={this.props.role == "option" ? this.props.setSize : undefined}
                aria-posinset={this.props.role == "option" ? this.props.itemIndex + 1 : undefined}
                className={css("vss-PickList--row", this.props.disabled && "disabled", this.props.firstInGroup && "first-in-group")}
                ref={(rowDiv: HTMLDivElement) => (this._rowDiv = rowDiv)}
                data-is-focusable={true}
                onKeyDown={this._onKeyDown}
                data-selection-key={this.props.dataSelectionKey}
            >
                <FocusZone direction={FocusZoneDirection.horizontal}>{this.props.children}</FocusZone>
            </div>
        );
    }

    private _onKeyDown = (ev: React.KeyboardEvent<HTMLElement>): void => {
        if (ev.which === KeyCode.enter || (ev.which === KeyCode.space && this.props.spaceBarTriggersClick)) {
            const target = ev.target as HTMLElement;
            if (target === this._rowDiv) {
                if (this.props.href) {
                    if (ev.ctrlKey || ev.shiftKey) {
                        // Shift-enter or Ctrl-enter. Open in new window/tab
                        window.open(this.props.href, "_blank", "noopener");
                    } else {
                        // Regular click - invoke handler
                        if (!this.props.onKeyboardEnter()) {
                            // Not handled, perform navigation
                            window.location.href = this.props.href;
                        }
                    }
                } else {
                    // No hyperlink. Always invoke the change handler.
                    this.props.onKeyboardEnter();
                }
            }
        }
        if (ev.which === KeyCode.leftArrow) {
            const target = ev.target as HTMLElement;
            if (target.id === getFirstFocusable(this._rowDiv, target, true).id) {
                this._rowDiv.focus();
            }
        }
    };
}

interface IPickListElementProps<T> extends IBaseProps {
    item: IPickListItemInternal<T>;
    indicators?: IItemIndicatorProps[];
    onSelectionChanged?: (isSelected?: boolean) => boolean;
    onRenderItemText?: (item: IPickListItemInternal<T>, data: T) => JSX.Element;
}

class PickListSelectableElement<T> extends React.Component<IPickListElementProps<T>> {
    public render(): JSX.Element {
        const { item, indicators } = this.props;

        const itemContent = (
            <span className="vss-PickList--selectableElementButton-text">
                {this.props.onRenderItemText ? this.props.onRenderItemText(item, item.data) : item.name}
            </span>
        );

        const children: JSX.Element[] = [];
        if (item.iconProps) {
            let className = "vss-PickList--selectableElementButtonIcon";
            if (item.iconProps.className) {
                className = css(className, item.iconProps.className);
            }
            children.push(<VssIcon key="icon" {...item.iconProps} className={className} />);
        } else if (item.onRenderIcon) {
            children.push(
                <span key="icon" className={"vss-PickList--selectableElementButtonIcon"}>
                    {item.onRenderIcon()}
                </span>
            );
        }
        children.push(
            <div key="text-container" className="vss-PickList--selectableElementButton-textContainer">
                <TooltipHost
                    content={item.tooltipText || item.name}
                    overflowMode={item.tooltipText && item.tooltipText !== item.name ? undefined : TooltipOverflowMode.Parent}
                >
                    {itemContent}
                </TooltipHost>
            </div>
        );
        const disabled: boolean = typeof item.disabled === "function" ? item.disabled() : item.disabled;
        return (
            <div className={css("vss-PickList--element", "vss-PickList--selectableElement", { "is-selected": item.isSelected, disabled: disabled })}>
                {item.href ? (
                    <a
                        className="vss-PickList--selectableElementButton"
                        tabIndex={-1}
                        data-is-focusable={false}
                        href={item.href}
                        onClick={this._onClick}
                    >
                        {children}
                    </a>
                ) : (
                    <div
                        className="vss-PickList--selectableElementButton"
                        role="button"
                        onClick={this._onClick}
                        tabIndex={-1}
                        data-is-focusable={false}
                    >
                        {children}
                    </div>
                )}
                <ItemIndicatorSet indicators={indicators} onClick={this._onClick} />
            </div>
        );
    }

    private _onClick = (ev: React.MouseEvent<HTMLDivElement | HTMLElement>): void => {
        const { item, onSelectionChanged } = this.props;
        if (onSelectionChanged) {
            if (item.href && (ev.ctrlKey || ev.shiftKey || ev.button === 1 || ev.button === 2)) {
                // No-op. This is an item that has a hyperlink AND this is a
                // ctrl/shift-click event or middle/right-click event. Let the browser handle this
            } else {
                if (onSelectionChanged()) {
                    ev.preventDefault();
                }
            }
        }
    };
}

class PickListCheckboxElement<T> extends React.Component<IPickListElementProps<T>> {
    private tooltip = React.createRef<TooltipHost>();

    //Getting state of parent element on focus
    public rowFocusState(stat: boolean) {
        if (this.tooltip && this.tooltip.current) {
            this.tooltip.current.showTooltip(stat);
        }
    }

    public render(): JSX.Element {
        const { item, indicators } = this.props;
        const icon = item.iconProps ? (
            <VssIcon className="vss-PickList--multiSelectIcon" {...item.iconProps} />
        ) : item.onRenderIcon ? (
            <span className="vss-PickList--multiSelectIcon">{item.onRenderIcon()}</span>
        ) : null;
        const disabled: boolean = typeof item.disabled === "function" ? item.disabled() : item.disabled;
        const label = (
            <div className="vss-PickList--multiSelectTextContainer">
                <TooltipHost ref={this.tooltip} overflowMode={TooltipOverflowMode.Parent} content={item.name}>
                    <span className={css("vss-PickList--multiSelectText", disabled && "disabled")}>{item.name}</span>
                </TooltipHost>
            </div>
        );

        const indicatorSet = <ItemIndicatorSet indicators={indicators} />;

        return (
            <div className={css("vss-PickList--element", "vss-PickList--multiSelectableElement")} onClick={this._onClick}>
                <Checkbox className="vss-PickList--checkbox" checked={item.isSelected} disabled={disabled} ariaLabel={item.name} tabIndex={null} />
                {icon}
                {label}
                {indicatorSet}
            </div>
        );
    }

    private _onClick = (ev: React.FormEvent<HTMLDivElement>): void => {
        if (!ev.defaultPrevented) {
            this.props.onSelectionChanged!(!this.props.item.isSelected);
            ev.preventDefault();
        }
    };
}

interface IPickListActionProps extends IBaseProps {
    action: IPickListAction;
    setSize: number;
    itemIndex: number;
}

class PickListAction extends React.Component<IPickListActionProps> {
    public render(): JSX.Element {
        const { setSize, itemIndex } = this.props;
        const { href, iconProps, name, disabled, ariaLabel, tooltipText } = this.props.action;
        let actionContents = (
            <Button
                subtle={true}
                className="vss-PickList--selectableElementButton"
                onClick={this._onClick}
                disabled={disabled}
                href={href}
                excludeTabStop={true}
                excludeFocusZone={true}
                iconProps={iconProps ? VssIcon.getIconProps(iconProps, "vss-PickList--selectableElementButtonIcon") : undefined}
                tooltipProps={tooltipText ? { showOnFocus: true, text: tooltipText } : undefined}
            >
                {name}
            </Button>
        );

        if (tooltipText) {
            actionContents = (
                <TooltipHost content={tooltipText} hostClassName="vss-PickList--action-tooltip">
                    {actionContents}
                </TooltipHost>
            );
        }

        return (
            <div
                aria-label={ariaLabel !== undefined ? ariaLabel : name}
                aria-posinset={itemIndex + 1}
                aria-setsize={setSize}
                className="vss-PickList--action"
                data-is-focusable={true}
                role="option"
                onKeyDown={this._onKeyDown}
            >
                {actionContents}
            </div>
        );
    }

    private _onClick = (ev: any): void => {
        const { action } = this.props;
        if (action.onClick) {
            action.onClick(ev);
        }
    };

    private _onKeyDown = (ev: React.KeyboardEvent<HTMLElement>): void => {
        if (!this.props.action.disabled && (ev.which === KeyCode.enter || ev.which === KeyCode.space)) {
            const { action } = this.props;
            const ctrlOrShift = ev.ctrlKey || ev.shiftKey;

            // Invoke the click handler unless we have an href and ctrl/shift was pressed
            if (action.onClick && (!action.href || !ctrlOrShift)) {
                this.props.action.onClick(ev);
            } else if (action.href) {
                if (ctrlOrShift) {
                    window.open(action.href, "_blank", "noopener");
                } else {
                    window.location.href = action.href;
                }
            }
        }
    };
}

interface IPickListOperatorGroupProps extends IBaseProps {
    operators: IPickListOperator[];
    selectedOperatorKey: string;
    onChange: (selectedOperatorKey: string) => void;
}

class PickListOperatorGroup extends React.Component<IPickListOperatorGroupProps> {
    private _radioButton = React.createRef<RadioButton>();

    public render(): JSX.Element {
        const { operators, selectedOperatorKey } = this.props;
        const options = operators.map((operator, index) => {
            const ref = index === 0 ? this._radioButton : undefined;
            return <RadioButton text={operator.name} id={operator.key} ref={ref} key={operator.key} />;
        });
        return (
            <RadioButtonGroup
                selectedButtonId={selectedOperatorKey}
                onSelect={this._onSelect}
                excludeFocusZone={true}
                direction={RadioButtonGroupDirection.Horizontal}
            >
                {options}
            </RadioButtonGroup>
        );
    }

    public focus(): void {
        if (this._radioButton.current) {
            this._radioButton.current.focus();
        }
    }

    private _onSelect = (selectedButtonId: string): void => {
        this.props.onChange(selectedButtonId);
    };
}

interface DelayedSpinnerState {
    isVisible: boolean;
}

class DelayedSpinner extends React.PureComponent<ISpinnerProps, DelayedSpinnerState> {
    private timeoutId: number;
    public state: DelayedSpinnerState = { isVisible: false };

    public componentDidMount(): void {
        this.timeoutId = window.setTimeout(() => {
            this.setState({ isVisible: true });
            this.timeoutId = -1;
        }, HIDE_LOADING_ITEMS_TIME_MS);
    }

    public componentWillUnmount(): void {
        window.clearTimeout(this.timeoutId);
    }

    public render(): JSX.Element {
        return this.state.isVisible ? <Spinner {...this.props} /> : null;
    }
}
